import sortBy from 'lodash/sortBy';

import type { CoursePrice, CourseRichSearchCard } from 'bundles/seo/components/course-rich-search-card/types';
import { CoursePrices } from 'bundles/seo/components/course-rich-search-card/types';
import { toEducationLevelValue } from 'bundles/seo/utils/courseInfoSchemaMarkupUtils';
import { ENROLLMENT_COUNT_THRESHOLD } from 'bundles/unified-description-page-common/constants';
import type { ConsumerCoursePageFieldsFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/ConsumerCoursePageQuery';

function extractTextTags(input: string): string[] {
  const regex = /<text>(.*?)<\/text>/g;
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return Array.from(input.matchAll(regex), (match) => match[1]);
}

function toEducationalLevel(level: ConsumerCoursePageFieldsFragment['difficultyLevel']) {
  return {
    educationalLevel: toEducationLevelValue(level),
  } as const;
}

function toLanguages(pageData: ConsumerCoursePageFieldsFragment) {
  const languages = [
    ...pageData.primaryLanguages,
    ...(pageData.subtitleLanguages ?? []),
    ...pageData.translatedLanguages.map((l) => l.code),
  ]
    .filter((l) => l.length >= 2)
    .map((l) => l.substring(0, 2));
  if (languages.length) {
    const inLanguage = languages[0];
    const restLanguages = new Set(languages.slice(1));
    // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
    restLanguages.delete(inLanguage);
    const availableLanguage = sortBy(Array.from(restLanguages));
    return {
      ...(inLanguage ? { inLanguage } : {}),
      ...(availableLanguage.length ? { availableLanguage } : {}),
    };
  } else {
    return {};
  }
}

function toTotalHistoricalEnrollment(count: ConsumerCoursePageFieldsFragment['totalEnrollmentCount']) {
  if (count && count > ENROLLMENT_COUNT_THRESHOLD) {
    return {
      totalHistoricalEnrollment: count,
    };
  } else {
    return {};
  }
}

function toAggregateRating(ratings: ValidDescriptionPageCoursePage['ratings']) {
  return {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratings.averageFiveStarRating,
      bestRating: 5,
      ratingCount: ratings.ratingCount,
    },
  } as const;
}

function toProvider(partners: ValidDescriptionPageCoursePage['partners']) {
  return {
    provider: {
      '@type': 'Organization',
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      name: partners[0].name,
      // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      url: partners[0].website,
    },
  } as const;
}

function toSyllabus(material: ValidDescriptionPageCoursePage['material']) {
  return {
    syllabusSections: material.weeks.map(
      ({ name, description, totalDuration }) =>
        ({
          '@type': 'Syllabus',
          name,
          description: description ?? name,
          timeRequired: totalDuration,
        } as const)
    ),
  };
}

function toCoursePrerequisites(recommendations: ValidDescriptionPageCoursePage['cmlRecommendedBackground']) {
  return {
    coursePrerequisites: recommendations?.cmlValue ? extractTextTags(recommendations.cmlValue) : [],
  };
}

function toReviews(topReviews: ConsumerCoursePageFieldsFragment['topReviews']) {
  if (topReviews && topReviews?.length) {
    return {
      review: topReviews.slice(0, 5).map(
        (r) =>
          ({
            '@type': 'Review',
            author: {
              '@type': 'Person',
              name: r.authorName,
            },
            datePublished: r.timestamp,
            reviewBody: r.comment,
            reviewRating: {
              '@type': 'Rating',
              bestRating: '5',
              ratingValue: r.rating,
            },
          } as const)
      ),
    } as const;
  } else {
    return {};
  }
}

function toHasCourseInstances(pageData: ValidDescriptionPageCoursePage) {
  const instructor = pageData.instructors?.[0];
  return {
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        courseWorkload: pageData.material.totalDuration,
        ...(instructor
          ? ({
              instructor: {
                '@type': 'Person',
                name: instructor.fullName,
                ...(instructor.department ? { description: instructor.department } : {}),
                ...(instructor.photo ? { image: instructor.photo } : {}),
              },
            } as const)
          : {}),
      } as const,
    ],
  };
}

const staticMetadata = {
  '@context': 'https://schema.org/',
  '@type': 'Course',
  publisher: {
    '@type': 'Organization',
    name: 'Coursera',
    url: 'https://www.coursera.org',
  },
  financialAidEligible: 'Financial Aid available for eligible students.',
  educationalCredentialAwarded: {
    '@type': 'EducationalOccupationalCredential',
    name: 'Coursera Certificate',
  },
} as const;

type RequiredNotNull<T> = {
  [P in keyof T]-?: NonNullable<T[P]>;
};

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: NonNullable<T[P]> };

type ValidDescriptionPageCoursePage = WithRequired<
  ConsumerCoursePageFieldsFragment,
  'partners' | 'material' | 'photoUrl' | 'ratings'
> & { ratings: RequiredNotNull<ConsumerCoursePageFieldsFragment['ratings']> };

function isValidDescriptionPageCoursePage(
  pageData: ConsumerCoursePageFieldsFragment
): pageData is ValidDescriptionPageCoursePage {
  return (
    !!pageData.partners &&
    pageData.partners?.length > 0 &&
    !!pageData.material &&
    !!pageData.photoUrl &&
    pageData.photoUrl.length > 0 &&
    !!pageData.ratings &&
    !!pageData.ratings.averageFiveStarRating &&
    !!pageData.ratings.ratingCount
  );
}

function getOfferDescription(price: CoursePrice) {
  switch (price) {
    case CoursePrices.Free:
      return 'Free';
    case CoursePrices.PartOfCourseraPlus:
      return 'Subscription';
    case CoursePrices.Auditable:
    default:
      return 'Partially Free';
  }
}

export function fromDescriptionPageCoursePage(
  pageData: ConsumerCoursePageFieldsFragment,
  coursePrice: CoursePrice,
  url: string
): CourseRichSearchCard | undefined {
  if (!isValidDescriptionPageCoursePage(pageData)) {
    return undefined;
  }

  const offerDesc = getOfferDescription(coursePrice);

  if (!offerDesc) {
    return undefined;
  }

  // @ts-expect-error URL: string
  return {
    ...staticMetadata,

    '@id': url,
    name: pageData.name,
    description: (pageData.description || pageData.name).substring(0, 500),

    image: [pageData.photoUrl],
    about: pageData.skills ?? [],
    teaches: pageData.cmlLearningObjectives?.flatMap((objective) => extractTextTags(objective.cmlValue)) ?? [],

    offers: {
      '@type': 'Offer',
      category: offerDesc,
    },

    ...toAggregateRating(pageData.ratings),
    ...toProvider(pageData.partners),
    ...toEducationalLevel(pageData.difficultyLevel),
    ...toTotalHistoricalEnrollment(pageData.totalEnrollmentCount),
    ...toLanguages(pageData),
    ...toSyllabus(pageData.material),
    ...toCoursePrerequisites(pageData.cmlRecommendedBackground),
    ...toReviews(pageData.topReviews),
    ...toHasCourseInstances(pageData),
  };
}
